/**
 * @file Renders a modal that requires the user to confirm a single
 * notice, or step through a wizard of notices, before it runs a callback.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Button, Col, Row, Space, Steps } from "antd";

import { W0, W1 } from "@components/Constants";
import { Markdown } from "@components/Text";

import Modal from "./Modal/Modal";
import type { ModalRef } from "./Modal/types";

const LABEL_OK = "I UNDERSTAND";
const LABEL_NEXT = "Next";
const LABEL_BACK = "Back";

type WizardItem = {
  title: string;
  content: ReactNode;
};

type ConfirmHandler = () => Promise<unknown> | unknown;

type Props = {
  buttonTitle?: string;
  title: ReactNode;
  modal: ModalRef;
  content: string | WizardItem[];
};

/**
 * Renders the confirmation modal that `modal.current.open` shows, either
 * a single notice or a multi-step wizard, and runs the confirm callback.
 */
const UnderstandConfirmModal = ({
  buttonTitle = LABEL_OK,
  title,
  modal,
  content,
}: Props): JSX.Element => {
  const [ onOk, setOnOk ] = useState<ConfirmHandler | false>(false);
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ currentStep, setCurrentStep ] = useState(0);

  /**
   * Resets the modal's loading state, step and open state.
   */
  const onClose = () => {
    setIsLoading(false);
    setCurrentStep(0);
    setIsOpen(false);
  };

  /**
   * Runs the confirm callback, then closes the modal.
   */
  const onConfirm = async () => {
    setIsLoading(true);

    if (onOk) {
      await onOk();
    }

    onClose();
  };

  /**
   * Opens the modal and stores the callback to run on confirm.
   */
  modal.current.open = (callback: ConfirmHandler = () => undefined) => {
    setIsLoading(false);
    setIsOpen(true);
    setOnOk(() => callback);
  };

  const shouldRenderWizard = Array.isArray(content);
  const wizardItems = shouldRenderWizard ? content : [];
  const markdownText = shouldRenderWizard ? "" : content;
  const contentItems = content.length;
  const hasBack = currentStep > 0;
  const hasNext = currentStep < contentItems - 1;
  const hasDone = currentStep === contentItems - 1;

  /**
   * Advances the wizard to the next step.
   */
  const next = () =>
    setCurrentStep(currentStep + 1);

  /**
   * Moves the wizard back to the previous step.
   */
  const prev = () =>
    setCurrentStep(currentStep - 1);

  const items = wizardItems
    .map((item) => ({
      key: item.title,
      title: item.title,
    }));

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onClose={onClose}
    >
      {
        !shouldRenderWizard && (
          <Space
            direction="vertical"
            style={{ marginTop: -1 * W1, marginBottom: W0 }}
          >
            <Markdown text={markdownText} />
            <center>
              <Button
                type="primary"
                onClick={onConfirm}
                loading={isLoading}
              >
                {buttonTitle}
              </Button>
            </center>
          </Space>
        )
      }
      {
        shouldRenderWizard && (
          <Space direction="vertical">
            <Steps current={currentStep} items={items} />

            <div>
              {wizardItems[currentStep].content}
            </div>

            <Row>
              <Col flex="auto">
                {hasBack && (
                  <Button onClick={() => prev()}>
                    {LABEL_BACK}
                  </Button>
                )}
              </Col>

              <Col style={{ textAlign: "right" }}>
                {hasNext && (
                  <Button type="primary" onClick={() => next()}>
                    {LABEL_NEXT}
                  </Button>
                )}

                {
                  hasDone && (
                    <Button
                      type="primary"
                      loading={isLoading}
                      onClick={onConfirm}
                    >
                      {buttonTitle}
                    </Button>
                  )
                }
              </Col>
            </Row>
          </Space>
        )
      }
    </Modal>
  );
};

export default UnderstandConfirmModal;
